import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { publicTalkerSafeReviews } from '../../util/talkerPublicProfile';
import {
  hydrateReviewAuthor,
  reviewAuthorInitials,
  reviewAuthorPhotoUrl,
  reviewAuthorScreenName,
} from '../../util/reviewAuthor';

import ReviewRating from '../ReviewRating/ReviewRating';

import css from './Reviews.module.css';

/**
 * Reviewer thumb: listing/profile photo as a plain img (same as v459/v461
 * Featured thumbs). Initials only when there is no URL. Never Avatar /
 * ResponsiveImage — those barrel imports blanked iPhone Safari.
 *
 * @param {Object} props
 * @param {string} [props.photoUrl]
 * @param {string} [props.name]
 * @param {string} [props.initials]
 * @returns {JSX.Element}
 */
export const ReviewAuthorThumb = props => {
  const { photoUrl, name, initials } = props;
  if (photoUrl) {
    return (
      <img
        className={css.avatarPhoto}
        src={photoUrl}
        alt={name || ''}
        data-review-author-photo="true"
      />
    );
  }
  return (
    <span className={css.avatarInitials} data-review-author-initials="true">
      {initials || ''}
    </span>
  );
};

const Review = props => {
  const { review, intl } = props;
  const hydrated = hydrateReviewAuthor(review);
  const name = reviewAuthorScreenName(hydrated) || reviewAuthorScreenName(review);
  const photoUrl = reviewAuthorPhotoUrl(hydrated) || reviewAuthorPhotoUrl(review);
  const initials =
    reviewAuthorInitials(name) || hydrated?.author?.attributes?.profile?.abbreviatedName || '';

  const date = review.attributes.createdAt;
  const dateString = intl.formatDate(date, { month: 'long', year: 'numeric' });

  return (
    <div className={css.review}>
      <div className={css.avatar} data-review-author-avatar="true">
        <ReviewAuthorThumb photoUrl={photoUrl} name={name} initials={initials} />
      </div>
      <div>
        <ReviewRating
          rating={review.attributes.rating}
          className={css.mobileReviewRating}
          reviewStarClassName={css.reviewRatingStar}
        />
        <p className={css.reviewContent}>{review.attributes.content}</p>
        <p className={css.reviewInfo}>
          <span data-review-author="true">{name}</span>
          <span className={css.separator}>•</span>
          {dateString}
          <span className={css.desktopSeparator}>•</span>
          <span className={css.desktopReviewRatingWrapper}>
            <ReviewRating
              rating={review.attributes.rating}
              className={css.desktopReviewRating}
              reviewStarClassName={css.reviewRatingStar}
            />
          </span>
        </p>
      </div>
    </div>
  );
};

/**
 * A component that renders a list of reviews.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.review>} props.reviews - The reviews to render
 * @returns {JSX.Element}
 */
const Reviews = props => {
  const intl = useIntl();
  const { className, rootClassName, reviews = [] } = props;
  const classes = classNames(rootClassName || css.root, className);
  const visible = publicTalkerSafeReviews(reviews);

  return visible.length ? (
    <ul className={classes}>
      {visible.map(r => {
        return (
          <li key={`Review_${r.id.uuid}`} className={css.reviewItem}>
            <Review review={r} intl={intl} />
          </li>
        );
      })}
    </ul>
  ) : null;
};

export default Reviews;
